import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { Spinner } from '../../components/atoms'
import { useAuth } from '../../hooks/useAuth'
import { ROUTES } from '../types'

interface AuthGuardProps {
  children: ReactNode
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading, checkAuth, authState } = useAuth()
  const location = useLocation()
  const [hasChecked, setHasChecked] = useState(false)

  useEffect(() => {
    const verify = async () => {
      await checkAuth()
      setHasChecked(true)
    }

    verify()
  }, [checkAuth])

  if (isLoading || !hasChecked) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Spinner size="lg" label="Verifying authentication..." />
      </div>
    )
  }

  if (authState === 'tenant_suspended') {
    return <Navigate to={ROUTES.ACCOUNT_SUSPENDED} replace />
  }

  if (authState === 'tenant_deleted') {
    return <Navigate to={ROUTES.ACCOUNT_DELETED} replace />
  }

  if (authState === 'tenant_not_found') {
    return <Navigate to={ROUTES.ACCOUNT_NOT_FOUND} replace />
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />
  }

  return <>{children}</>
}
